import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Code, Group, NumberInput, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { createLicensePlates, LICENSE_PLATE_MAX_BATCH, type LicensePlateDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Creates a run of plates and shows the numbers it produced.
 *
 * Showing them matters. In the legacy system these numbers went straight to a label printer and
 * were recorded nowhere — sp_WebLicensePlate_GetForPrinting allocates and returns them without
 * writing a row — so a failed print left numbers consumed with nothing to show which. Here the
 * plates exist as soon as they are created, and the numbers stay on screen so they can be
 * written on a pallet by hand if the printer is not the next step.
 *
 * Label printing itself is not wired yet: legacy also returns a per-plate label count from
 * tblRefCat row 107, which belongs with the label template engine rather than here.
 */
export function CreateLicensePlatesModal({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [count, setCount] = useState<number | ''>(1);
  const [created, setCreated] = useState<LicensePlateDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);

  // Reset on open, not on close: leaving the numbers on screen while the modal fades out is
  // better than blanking them the instant it starts closing.
  useEffect(() => {
    if (opened) {
      setCount(1);
      setCreated([]);
      setError(null);
      setCorrelationId(null);
    }
  }, [opened]);

  const invalidCount = count === '' || count < 1 || count > LICENSE_PLATE_MAX_BATCH;

  async function submit() {
    if (invalidCount) return;
    setSaving(true);
    setError(null);
    setCorrelationId(null);
    try {
      const plates = await createLicensePlates(count as number);
      setCreated(plates);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (e instanceof ApiError) setCorrelationId(e.problem.correlationId ?? null);
    } finally {
      setSaving(false);
    }
  }

  const done = created.length > 0;

  return (
    <AppModal opened={opened} onClose={onClose} width="md" title={t('licensePlates.createTitle')}>
      <Stack gap="md">
        {!done && (
          <>
            <Text size="sm" c="dimmed">
              {t('licensePlates.createHint')}
            </Text>
            {/* No min/max on the input itself. Mantine clamps to those bounds, which would
                silently turn a typed 5000 into 1000 — the user would print a thousand labels
                having asked for five, and never be told. Out-of-range is explained instead, and
                Create stays disabled until it is fixed. */}
            <NumberInput
              label={t('licensePlates.howMany')}
              value={count}
              onChange={(v) => setCount(v === '' ? '' : Number(v))}
              allowDecimal={false}
              allowNegative={false}
              w={200}
              error={
                count !== '' && (count < 1 || count > LICENSE_PLATE_MAX_BATCH)
                  ? t('licensePlates.errors.countRange', { max: LICENSE_PLATE_MAX_BATCH })
                  : undefined
              }
            />
          </>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            <Stack gap={4}>
              <Text size="sm">{error}</Text>
              {correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{correlationId}</Code>
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        {done && (
          <Alert
            icon={<IconCheck size={16} />}
            color="green"
            variant="light"
            title={t('licensePlates.createdTitle', { plates: created.length })}
          >
            <Stack gap="xs">
              <Text size="sm">{t('licensePlates.createdBody')}</Text>
              <Group gap={6} wrap="wrap">
                {created.map((p) => (
                  <Code key={p.licensePlateId}>{p.licensePlateNumber}</Code>
                ))}
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          {/* 'done' rather than reusing the retire wording: "Close" on this button means dismiss
              the dialog, while "Close" in the row menu retires a plate. Same word, different act. */}
          <Button variant="default" onClick={onClose}>
            {done ? t('licensePlates.done') : t('shell.cancel')}
          </Button>
          {!done && (
            <Button color="invetrak" loading={saving} disabled={invalidCount} onClick={() => void submit()}>
              {t('licensePlates.create')}
            </Button>
          )}
        </Group>
      </Stack>
    </AppModal>
  );
}
